"use no memo";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "@tanstack/react-router";
import X from "~icons/ph/x";
import type { GroupInvitation } from "../../shared/types";
import { useAppLocation } from "../app/location";
import { useDismissGroupInvitation } from "../data/mutations";
import { t } from "../i18n";
import { closeGroupNotifications } from "../notifications/push";
import { Avatar, useTopModal } from "../ui/components";
import { notify } from "../ui/toast";

export function GroupInvitationCard({
  invitation,
}: {
  invitation: GroupInvitation;
}) {
  const navigate = useNavigate(),
    dismiss = useDismissGroupInvitation();
  async function close() {
    try {
      await dismiss.mutateAsync({ link: invitation.link });
      void closeGroupNotifications(invitation.link).catch(() => {});
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error));
    }
  }
  return (
    <div className="group-invitation-card">
      <Avatar person={invitation.organizer} />
      <div className="group-invitation-text">
        <strong>
          <bdi>{invitation.organizer.name}</bdi>
        </strong>
        <p>
          {t(
            invitation.ended
              ? "انتهت هذه المكالمة الجماعية."
              : "دعوة إلى مكالمة جماعية",
          )}
        </p>
      </div>
      {!invitation.ended && (
        <button
          className="button small"
          onClick={() => {
            void navigate({
              to: "/group/$groupCode",
              params: { groupCode: invitation.link },
              search: true,
            });
            void close();
          }}
        >
          {t("انضمام")}
        </button>
      )}
      <button
        className="icon-button"
        disabled={dismiss.isPending}
        aria-label={t("إغلاق التنبيه")}
        onClick={() => void close()}
      >
        <X width={18} height={18} />
      </button>
    </div>
  );
}

// A quiet, persistent notice. It stays reachable over a native dialog without
// stealing focus, and missed socket events are recovered by the account query.
export function GroupInvitationNotice({
  invitations,
}: {
  invitations: GroupInvitation[];
}) {
  const topModal = useTopModal(),
    { screen, groupCode } = useAppLocation();
  const invitation = invitations.find((invite) => invite.link !== groupCode);
  useEffect(() => {
    const close = () => {
      if (!document.hidden && invitations.length)
        void closeGroupNotifications().catch(() => {});
    };
    close();
    navigator.serviceWorker?.addEventListener("message", close);
    document.addEventListener("visibilitychange", close);
    return () => {
      navigator.serviceWorker?.removeEventListener("message", close);
      document.removeEventListener("visibilitychange", close);
    };
  }, [invitations]);
  if (!invitation || screen === "notifications") return null;
  const notice = (
    <aside
      className="group-invitation-notice"
      aria-label={t("دعوة إلى مكالمة جماعية")}
    >
      <div role="status">
        {invitation.ended
          ? t("انتهت هذه المكالمة الجماعية.")
          : t("{name} يدعوك إلى مكالمة جماعية", {
              name: invitation.organizer.name,
            })}
      </div>
      <GroupInvitationCard invitation={invitation} />
    </aside>
  );
  return topModal ? createPortal(notice, topModal) : notice;
}
